// redux/slices/projectSlice.ts
import { createSlice } from '@reduxjs/toolkit';
import { ProjectState } from '../types/projectTypes';
import {
  fetchProjects,
  loadProjects,
  createProject,
  updateProject,
  fetchProjectsByStatus,
  fetchProjectCount,
  fetchDashboardMetrics,
  searchProjects,
  fetchOriginProject,
  fetchReopenedProject,
} from '../actions/projectAction';

// Load initial state from localStorage if available
const getInitialState = (): ProjectState => {
  return {
    projects: [],
    pagination: null,
    loading: false,
    error: null,
    noProjectsFound: false,
    filteredProjects: {},
    projectCount: 0,
    lastProject: undefined,
    lastLoadTime: 0,
    totalAmount: 0,
    paymentReceived: 0,
    billedAmount: 0,
    pipelineAmount: 0,
    statusCounts: {},
    dashboardMetrics: undefined, // New field for all-projects dashboard metrics
    dashboardLoading: false,
    dashboardError: null,
    dashboardMetricsCache: {
      timestamp: 0,
      cacheKey: '',
    },
    originProject: null,
    originProjectLoading: false,
    originProjectError: null,
    reopenedProject: null,
    reopenedProjectLoading: false,
    reopenedProjectError: null,
  };
};

const initialState = getInitialState();

const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    clearProjectsCache: state => {
      state.noProjectsFound = false;
      try {
        localStorage.removeItem('projects');
      } catch (error) {
        console.error('Failed to clear projects from localStorage:', error);
      }
    },
    clearCache: state => {
      state.lastLoadTime = 0;
      state.filteredProjects = {};
    },
    clearStatusData: state => {
      // Clear current projects display
      state.projects = [];
      // Clear filtered projects cache
      state.filteredProjects = {};
      // Reset pagination
      state.pagination = null;
      // Reset flags
      state.noProjectsFound = false;
      // IMPORTANT: Clear lastLoadTime to force fresh API calls
      state.lastLoadTime = 0;
      // Set loading to true to show loading state
      state.loading = true;
      // Clear any errors
      state.error = null;
    },
    clearOriginProject: state => {
      state.originProject = null;
      state.originProjectLoading = false;
      state.originProjectError = null;
    },
    clearReopenedProject: state => {
      state.reopenedProject = null;
      state.reopenedProjectLoading = false;
      state.reopenedProjectError = null;
    },
  },
  extraReducers: builder => {
    builder
      // Handle fetchProjects action
      .addCase(fetchProjects.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload.data;
        state.pagination = action.payload.pagination;

        // Check if we got an empty array with a successful response
        if (Array.isArray(action.payload.data) && action.payload.data.length === 0) {
          state.noProjectsFound = true;
        } else {
          state.noProjectsFound = false;
        }

        // Save to localStorage
        try {
          localStorage.setItem(
            'projects',
            JSON.stringify({
              projects: action.payload.data,
              pagination: action.payload.pagination,
              noProjectsFound: state.noProjectsFound,
              filteredProjects: state.filteredProjects,
              timestamp: new Date().getTime(),
            })
          );
        } catch (error) {
          console.error('Failed to save projects to localStorage:', error);
        }
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Handle the loadProjects action
      .addCase(loadProjects.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadProjects.fulfilled, (state, action) => {
        state.loading = false;

        // Handle data from cache or fresh fetch
        if (action.payload.fromCache) {
          console.log('Using cached projects from Redux store');
          // Make sure noProjectsFound is set correctly - even for cached data
          state.noProjectsFound = Array.isArray(state.projects) && state.projects.length === 0;
        } else {
          console.log('Setting fresh projects data in Redux');
          // Update with fresh data
          state.projects = action.payload.data || [];
          // Set noProjectsFound flag based on data length
          state.noProjectsFound =
            Array.isArray(action.payload.data) && action.payload.data.length === 0;
          // Update last load time
          state.lastLoadTime = action.payload.lastLoadTime || Date.now();

          // Also update pagination information if provided
          if (action.payload.pagination) {
            state.pagination = action.payload.pagination;
            console.log('Updated Redux pagination:', {
              page: action.payload.pagination.page,
              total: action.payload.pagination.total,
              pages: action.payload.pagination.pages,
            });
          }
        }
      })
      .addCase(loadProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Handle project status filter
      .addCase(fetchProjectsByStatus.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectsByStatus.fulfilled, (state, action) => {
        state.loading = false;

        // Store the filtered projects by status
        if (action.payload && action.payload.status) {
          const status = action.payload.status;
          state.filteredProjects = {
            ...state.filteredProjects,
            [status]: action.payload.data,
          };

          // For the current view, also update the main projects list
          // This makes the current UI seamless without requiring additional logic
          if (Array.isArray(action.payload.data)) {
            state.projects = action.payload.data;

            // Set the noProjectsFound flag based on the actual data received
            state.noProjectsFound = action.payload.data.length === 0;
            console.log(`No projects found for status ${status}:`, state.noProjectsFound);
          }

          // Update pagination with the data from the filtered results
          if (action.payload.pagination) {
            state.pagination = action.payload.pagination;
            console.log('Updated pagination for status filter:', state.pagination);
          }
        }

        // Save to localStorage including filtered status projects
        try {
          localStorage.setItem(
            'projects',
            JSON.stringify({
              projects: state.projects,
              pagination: state.pagination,
              noProjectsFound: state.noProjectsFound,
              filteredProjects: state.filteredProjects,
              timestamp: new Date().getTime(),
            })
          );
        } catch (error) {
          console.error('Failed to save projects to localStorage:', error);
        }
      })
      .addCase(fetchProjectsByStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Handle project creation
      .addCase(createProject.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.loading = false;
        // Add the new project to the projects array if it was returned
        if (action.payload.data?.project) {
          state.projects.unshift(action.payload.data.project);
          state.noProjectsFound = false;

          // Clear the filtered projects since data is now stale
          state.filteredProjects = {};
        }

        // Update localStorage
        try {
          localStorage.setItem(
            'projects',
            JSON.stringify({
              projects: state.projects,
              pagination: state.pagination,
              noProjectsFound: false,
              filteredProjects: state.filteredProjects,
              timestamp: new Date().getTime(),
            })
          );
        } catch (error) {
          console.error('Failed to save projects to localStorage:', error);
        }
      })
      .addCase(createProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Handle project update
      .addCase(updateProject.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.loading = false;

        // Log the response data for debugging
        console.log('Project update response:', action.payload);

        // Find and update the project in the projects array
        if (action.payload.data?.project || action.payload.data) {
          const updatedProject = action.payload.data?.project || action.payload.data;
          const projectId = updatedProject.id;

          // Find the project in the state
          const projectIndex = state.projects.findIndex(project => project.id === projectId);

          if (projectIndex !== -1) {
            // Get the current project from state
            const currentProject = state.projects[projectIndex];

            // Extract the latest status from projectAssigned if available
            let latestStatus = currentProject.status; // default to current status

            // Check if the incoming data has projectAssigned array
            if (updatedProject.assignedEmployees && updatedProject.assignedEmployees.length > 0) {
              // Backend sometimes returns the assignments as assignedEmployees
              const sortedAssignments = [...updatedProject.assignedEmployees].sort(
                (a, b) =>
                  new Date(b.assignedDateAndTime || 0).getTime() -
                  new Date(a.assignedDateAndTime || 0).getTime()
              );

              if (sortedAssignments[0] && sortedAssignments[0].status) {
                latestStatus = sortedAssignments[0].status;
                console.log('Found latest status in assignedEmployees:', latestStatus);
              }
            } else if (
              updatedProject.projectAssigned &&
              updatedProject.projectAssigned.length > 0
            ) {
              // Sort projectAssigned by date (newest first) to get the latest status
              const sortedAssignments = [...updatedProject.projectAssigned].sort((a, b) => {
                const dateA = new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime();
                const dateB = new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime();
                return dateA - dateB;
              });

              if (sortedAssignments[0]) {
                latestStatus = sortedAssignments[0].status;
                console.log('Found latest status in projectAssigned:', latestStatus);
              }
            } else if (updatedProject.status) {
              // If status is directly in the updated project, use that
              latestStatus = updatedProject.status;
              console.log('Using direct status from update response:', latestStatus);
            }

            // Check for finalStatus in the response (used in some API responses)
            if (updatedProject.finalStatus) {
              latestStatus = updatedProject.finalStatus;
              console.log('Using finalStatus from response:', latestStatus);
            }

            // Create the updated project with the latest status explicitly set
            const mergedProject = {
              ...currentProject,
              ...updatedProject,
              status: latestStatus, // Ensure status is explicitly set at top-level
            };

            // If there's no projectAssigned array or it's empty, create one with the latest status
            if (!mergedProject.projectAssigned || mergedProject.projectAssigned.length === 0) {
              mergedProject.projectAssigned = [
                {
                  status: latestStatus,
                  assignedDate: new Date().toISOString(),
                  assignedDateAndTime: new Date().toISOString(),
                },
              ];
            } else {
              // Make sure the first assignment has the latest status
              mergedProject.projectAssigned[0].status = latestStatus;
            }

            // Log the updated project for debugging
            console.log('Updated project with status:', mergedProject);

            // Update the project in state
            state.projects[projectIndex] = mergedProject;
          } else {
            console.warn(`Project with id ${projectId} not found in state`);

            // If we can't find the project, it's best to fetch fresh data
            // Just mark the cache as stale so next view will trigger a reload
            state.filteredProjects = {};
            return;
          }

          // Clear filtered projects cache since data has changed
          state.filteredProjects = {};
        }

        // Update localStorage
        try {
          localStorage.setItem(
            'projects',
            JSON.stringify({
              projects: state.projects,
              pagination: state.pagination,
              noProjectsFound: state.noProjectsFound,
              filteredProjects: {}, // Always clear filtered projects after an update
              timestamp: new Date().getTime(),
            })
          );
        } catch (error) {
          console.error('Failed to save projects to localStorage:', error);
        }
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Handle fetchDashboardMetrics thunk
      .addCase(fetchDashboardMetrics.pending, state => {
        // Don't set global loading state to true since this is a dashboard widget
        // We use a component-level loading state instead
        state.error = null;
        state.dashboardLoading = true;
      })
      .addCase(fetchDashboardMetrics.fulfilled, (state, action) => {
        // Store the dashboard metrics from the API
        state.dashboardLoading = false;
        state.dashboardMetrics = action.payload;

        // Update cache timestamp and key
        if (action.meta.arg) {
          state.dashboardMetricsCache = {
            timestamp: Date.now(),
            cacheKey: `${action.meta.arg.startDate}_${action.meta.arg.endDate}`,
          };
        } else {
          // If no date range was provided, use the default range
          const now = new Date();
          const defaultStartDate = new Date(now.getFullYear(), now.getMonth(), 1)
            .toISOString()
            .split('T')[0];
          const defaultEndDate = now.toISOString().split('T')[0];
          state.dashboardMetricsCache = {
            timestamp: Date.now(),
            cacheKey: `${defaultStartDate}_${defaultEndDate}`,
          };
        }

        // For backward compatibility, also update the legacy fields
        if (action.payload) {
          state.projectCount = action.payload.projectCount;
          state.paymentReceived = action.payload.paymentReceived;
          state.billedAmount = action.payload.billedAmount;
          state.pipelineAmount = action.payload.pipelineAmount;
          state.statusCounts = action.payload.statusCounts;
        }
      })
      .addCase(fetchDashboardMetrics.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload as string;
      })
      // Handle searchProjects thunk
      .addCase(searchProjects.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        if (action.payload && action.payload.data) {
          // Store the search results as projects
          state.projects = action.payload.data;

          // Update pagination with search results
          if (action.payload.pagination) {
            state.pagination = action.payload.pagination;
          }

          // Save search results to localStorage
          try {
            localStorage.setItem(
              'projects',
              JSON.stringify({
                projects: state.projects,
                pagination: state.pagination,
                noProjectsFound: state.noProjectsFound,
                filteredProjects: state.filteredProjects,
                timestamp: new Date().getTime(),
              })
            );
          } catch (error) {
            console.error('Failed to save search results to localStorage:', error);
          }
        } else {
          state.projects = [];
          state.noProjectsFound = true;
        }
      })
      .addCase(searchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Handle fetchOriginProject thunk
      .addCase(fetchOriginProject.pending, state => {
        state.originProjectLoading = true;
        state.originProjectError = null;
      })
      .addCase(fetchOriginProject.fulfilled, (state, action) => {
        state.originProjectLoading = false;
        state.originProject = action.payload;
        state.originProjectError = null;
      })
      .addCase(fetchOriginProject.rejected, (state, action) => {
        state.originProjectLoading = false;
        state.originProjectError = action.payload as string;
      })
      .addCase(fetchReopenedProject.pending, state => {
        state.reopenedProjectLoading = true;
        state.reopenedProjectError = null;
      })
      .addCase(fetchReopenedProject.fulfilled, (state, action) => {
        state.reopenedProjectLoading = false;
        state.reopenedProject = action.payload;
        state.reopenedProjectError = null;
      })
      .addCase(fetchReopenedProject.rejected, (state, action) => {
        state.reopenedProjectLoading = false;
        state.reopenedProjectError = action.payload as string;
      });
  },
});

export const { clearProjectsCache, clearCache, clearStatusData, clearOriginProject, clearReopenedProject } = projectSlice.actions;
export default projectSlice.reducer;
